import type { ReactNode } from 'react';

/**
 * Sidebar menu model.
 *
 * One model serves both shells:
 * - the classic RA2N accordion sidebar (single tab, groups with sub-items)
 * - the RAH shell from ConceptDesigns (一般 / 管理 tabs, icon rail + flyout)
 *
 * Menus are plain data so a tenant can supply its own tree from `customize/`
 * without touching the Sidebar component.
 */

/** A divider with a caption inside a group (RAH `.flyout-section`). */
export interface MenuSection {
  /** Caption text. Presence of this key is what marks the entry as a section. */
  section: string;
}

export interface MenuItem {
  label: string;
  path: string;
  icon?: ReactNode;
  /** Legacy absolute URL opened in a new tab (kept for existing manual links). */
  externalUrl?: string;
  openInNewTab?: boolean;
  /** Count badge (RAH `.fi-badge`). */
  badge?: string | number;
  /** Short tag such as `AI` (RAH `.fi-tag`). */
  tag?: string;
  /**
   * Listed but not navigable yet: the screen exists in the ConceptDesign and
   * has no RA2N route. Rendered greyed out rather than hidden, so the menu
   * doubles as the remaining build scope.
   */
  disabled?: boolean;
  /**
   * Restrict visibility to these clinic families (`clinic.parent_site_type`
   * falling back to `site_type`). Undefined = visible to every family.
   */
  families?: string[];
}

export type MenuEntry = MenuItem | MenuSection;

export interface MenuGroup {
  /** Stable id. Required by the RAH shell to track the open flyout. */
  id: string;
  label: string;
  icon: ReactNode;
  /** Accordion / flyout children. Mutually exclusive with `path`. */
  items?: MenuEntry[];
  /** Direct link when this group is a flat entry with no children. */
  path?: string;
  badge?: string | number;
  /** Flat group whose screen is not built yet. See `MenuItem.disabled`. */
  disabled?: boolean;
  families?: string[];
}

/** RAH splits the sidebar into 一般 / 管理. Classic menus declare a single tab. */
export interface MenuTab {
  id: string;
  label: string;
  groups: MenuGroup[];
}

export interface MenuDefinition {
  tabs: MenuTab[];
}

export const isMenuSection = (entry: MenuEntry): entry is MenuSection =>
  (entry as MenuSection).section !== undefined;

export const isMenuItem = (entry: MenuEntry): entry is MenuItem => !isMenuSection(entry);
